import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { API_HOST, TOKEN_KEY } from "./Config";
import { Modal } from "antd";
import { getTokenFromLocalCache } from "./Utils";


class Requester {

    instance: AxiosInstance = axios.create();
    baseURL: string = API_HOST;
    timeout: number = 30000;
    contentType: string = "application/json";
    validator: (response: AxiosResponse<any>) => boolean = _ => true;
    errorHandle: (response: AxiosResponse<any>) => void = Requester.clientErrorHandle;

    constructor() {
        this.instance.interceptors.request.use(request => {
            request.baseURL = this.baseURL;
            request.timeout = this.timeout;
            request.headers["content-type"] = this.contentType;
            const token = getTokenFromLocalCache();
            if (token) request.headers.common['Authorization'] = `Bearer ${ token }`;
            return request
        });
        this.instance.interceptors.response.use((response: any) => {
            if (!this.validator(response)) {
                this.errorHandle(response);
                return;
            }
            return response
        }, e => {
            const response = e.response;
            if (!this.validator(response)) {
                this.errorHandle(response);
                return;
            }
            return response
        });
    }

    static defaultErrorShow(response: AxiosResponse<any>) {
        if (response.status > 300 && response.status < 500) {
            Modal.error({
                title: "温馨提示",
                content: response.data ? response.data.msg : "网络请求异常",
            });
            return;
        }
        Modal.error({
            title: "网络请求异常",
            content: `code: ${ response.status }，message: ${ response.data ? response.data.msg : undefined }`,
        });
    }

    static clientErrorHandle(response: AxiosResponse<any>) {
        if (!response) return;
        if (response.status === 401) {
            // 登录失效
            localStorage.removeItem(TOKEN_KEY);
            window.location.href = `/login`;
            return;
        }
        Requester.defaultErrorShow(response);
    }

}


export function loginRequest(params: {
    account: string,
    password: string,
}): Promise<any> {
    const r = new Requester();
    r.validator = response => response.status === 201;
    return r.instance.post(`/p/authorizations`, params);
}

export function adminProfileRequest() {
    const r = new Requester();
    r.validator = response => response.status === 200;
    return r.instance.get(`/p/partner`);
}

export function invitersRequest() {
    const r = new Requester();
    r.validator = response => response.status === 200;
    return r.instance.get(`/p/inviters`);
}


export function usersRequest(params: {
    name?: string,
    id_number?: string,
    phone_number?: string,
    inviter?: string,
    create_start?: number
    create_end?: number
    page?: number,
    per_page?: number,
}) {
    const r = new Requester();
    r.validator = response => response.status === 200;
    return r.instance.get(`/p/users`, { params });
}

export function createUserRequest(params: any) {
    const r = new Requester();
    r.validator = response => response.status === 200;
    return r.instance.post(`/p/users`, params);
}

export function batchCreateUserRequest(params: any) {
    const r = new Requester();
    r.validator = response => response.status === 200;
    return r.instance.post(`/p/users/batch`, params);
}

export function ordersRequest(params: {
    channel: string,
    product_id?: string,
    partial: string,
    page?: number,
    per_page?: number
    order_by?: string, // "asc" | "desc"

    name?: string,
    id_number?: string,
    phone_number?: string,
    exam_config?: string,
    status?: number,
    start_at?: number,
    end_at?: number,
    inviter?: string,

    includes?: string, // "user,address,addition,..."
}) {
    const r = new Requester();
    r.validator = response => response.status === 200;
    return r.instance.get(`/p/orders`, { params });
}

export function materialsRequest(params: {
    channel: string,
    pay_start?: number,
    pay_end?: number,
    page?: number,
    per_page?: number,
}) {
    const r = new Requester();
    r.validator = response => response.status === 200;
    return r.instance.get(`/p/materials`, { params });
}

export function certProductsRequest() {
    const r = new Requester();
    r.validator = response => response.status === 200;
    return r.instance.get(`/p/certs`);
}

export function batchCreateOrdersRequest(params: {
    users: number[],
    create_at_channel: string,
    cert_id: string,
    exam_config_id: number,
    cert_attachment?: string,
    material_ids: string[],
}) {
    const r = new Requester();
    r.validator = response => response.status === 200;
    return r.instance.post(`/p/orders/batch`, params);
}

export function batchUploadOrdersPaymentAttachmentRequest(params: {
    orders: string[],
    attachment: string,
    amount: number,
}) {
    const r = new Requester();
    r.validator = response => response.status === 200;
    return r.instance.put(`/p/orders/payment/attachment/batch`, params);
}

export function batchUploadCertAttachmentRequest(params: {
    orders: string[],
    attachment: string,
}) {
    const r = new Requester();
    r.validator = response => response.status === 200;
    return r.instance.put(`/p/orders/cert/attachment/batch`, params);
}


export function examConfigsRequest(params?: {
    group?: string,
    channel?: string,

    exam_limit?: 1,
    apply_limit?: 1,
    check_count_limited?: 1,

    sort_desc?: 1,
    count?: number,
}) {
    const r = new Requester();
    r.validator = response => response.status === 200;
    return r.instance.get(`/p/cert/exam-configs`, { params: params || {} });
}
